import { useCallback, useMemo, useState } from 'react';

import { Grid } from '@mui/material';
import { GridRowProps } from '@mui/x-data-grid';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';

import { navigate } from '@redwoodjs/router';
import { MetaTags } from '@redwoodjs/web';

import DateFilterMenuItem from 'src/components/FilterDropDown/components/DateFilterMenuItem';
import {
    buildFilters,
    createFilterObject,
    DateRangeDetails,
} from 'src/components/FilterDropDown/utils';
import { NPIDataGrid, type NPIDataGridColDef } from 'src/components/NPIDataGrid/NPIDataGrid';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { GET_MERCHANTS_PAGINATED } from 'src/graphql/queries/getMerchantsPaginated.query';
import useStablePagination from 'src/hooks/useStablePagination';
import useTable from 'src/hooks/useTable';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { resolvePageSize } from 'src/lib/pagination';

const ViewMerchantsPage = () => {
    const {
        rows,
        pagination,
        fetchMore,
        updateSorting,
        updateFilters,
        loading,
        sortState,
        filterState,
        reset,
    } = useTable({
        query: GET_MERCHANTS_PAGINATED,
        pagination: {
            page: 1,
            pageSize: resolvePageSize(),
        },
        sorting: [],
        filterModel: { items: [] },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const buildColumns = (): NPIDataGridColDef[] => {
        return [
            {
                field: 'merchantAccountNumber',
                headerName: 'Merchant Account Number',
                flex: 1,
            },
            {
                field: 'applicationId',
                headerName: 'Application ID',
                flex: 1,
            },
            {
                field: 'mid',
                headerName: 'MID (Merchant ID)',
                flex: 1,
            },
            {
                field: 'tradingName',
                headerName: 'Trading Name',
                flex: 1,
            },
            {
                field: 'partnerName',
                headerName: 'Partner Name',
                flex: 1,
            },
            {
                field: 'dateAdded',
                headerName: 'Date Added',
                flex: 1,
                renderCell: (params: GridRenderCellParams) => {
                    const value = params?.value as string | Date | null | undefined;
                    const date = value ? dayjs(value) : null;
                    return <span>{date ? date.format('YYYY-MM-DD') : ''}</span>;
                },
            },
            {
                field: 'status',
                headerName: 'Status',
                flex: 1,
                renderCell: (params: GridRenderCellParams) => {
                    return <StatusChip status={(params.value as string) ?? ''} type="merchant" />;
                },
            },
        ];
    };

    const columns = useMemo(() => {
        return buildColumns();
    }, []);

    const handleAddMerchant = useCallback(() => {
        navigate('/merchant/add');
    }, []);

    const handleViewMerchant = useCallback(
        ({ merchantAccountNumber }: { merchantAccountNumber: string }) => {
            navigate(`/merchant/${merchantAccountNumber}`);
        },
        []
    );

    const handleOnClearFilters = () => {
        setFromDate(null);
        setToDate(null);
        reset();
    };

    const handleOnApplyFilters = () => {
        const dateDetails: DateRangeDetails = {
            field: 'dateAdded',
            fromDate: fromDate || null,
            toDate: toDate || null,
        };
        const model = createFilterObject(buildFilters(dateDetails));
        updateFilters(model);
    };

    const { stableTotalRows, stablePage } = useStablePagination(pagination, loading);

    return (
        <PageContent
            heading="Merchants"
            subHeading="Add, View, Remove and Update NPI Merchants as necessary"
        >
            <MetaTags title="Merchants" description="Merchants Page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    <NPIDataGrid
                        key="merchants-grid"
                        dataTestId="merchants-data-grid"
                        rows={rows}
                        columns={columns}
                        getRowId={(row: GridRowProps) => {
                            return row.id;
                        }}
                        serverSideFiltering={{
                            onFilterChange: updateFilters,
                            filterModel: filterState,
                        }}
                        serverSideSorting={{
                            onSortChange: updateSorting,
                            sortModel: sortState,
                        }}
                        pagination={{
                            isEnabled: true,
                            mode: 'server',
                            page: stablePage - 1,
                            pageSize: resolvePageSize(pagination.pageSize),
                            totalRows: stableTotalRows,
                            onPageChange: (page: number, pageSize: number) => {
                                fetchMore({ page: page + 1, pageSize });
                            },
                        }}
                        addFilterButtonConfig={{
                            isFilterActive: fromDate !== null || toDate !== null,
                            handleOnClearFilters,
                            handleOnApplyFilters,
                            filters: [
                                <DateFilterMenuItem
                                    key="merchant-date-filter"
                                    title="Date Added"
                                    fromDate={fromDate}
                                    toDate={toDate}
                                    handleFromDateChange={setFromDate}
                                    handleToDateChange={setToDate}
                                    onClearFilter={() => {
                                        setFromDate(null);
                                        setToDate(null);
                                    }}
                                />,
                            ],
                        }}
                        addRecordButtonConfig={{
                            addRecordButtonText: 'Add Merchant',
                            addRecordCallBack: handleAddMerchant,
                        }}
                        handleReset={reset}
                        filtering={{ isEnabled: true }}
                        customResetButtonLabel="Clear"
                        handleView={handleViewMerchant}
                        loading={loading}
                    />
                </Grid>
            </Grid>
        </PageContent>
    );
};

export default ViewMerchantsPage;
